import axios from 'axios';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import type { CustomerLoginResponse } from '@/types/customer-auth.types';

/**
 * Dedicated axios instance for refresh calls only — no interceptors, so it
 * cannot trigger the 401 → refresh loop and reads the envelope as-is.
 * Mirrors platform-token-refresh.ts's rationale exactly.
 */
const refreshClient = axios.create({
  baseURL: '/api/v1',
  timeout: 10_000,
  withCredentials: true,
});

let activeRefresh: Promise<string> | null = null;

/**
 * Refreshes the customer access token using the stored refresh token.
 * Concurrent callers share one in-flight request. Clears auth and throws on
 * failure (expired/revoked/reused refresh token).
 */
export function refreshCustomerAccessToken(): Promise<string> {
  if (activeRefresh !== null) return activeRefresh;

  activeRefresh = performRefresh().finally(() => {
    activeRefresh = null;
  });

  return activeRefresh;
}

async function performRefresh(): Promise<string> {
  const { refreshToken, clearAuth, rotateTokens } = useCustomerAuthStore.getState();

  if (!refreshToken) {
    clearAuth();
    throw new Error('No refresh token available');
  }

  const resp = await refreshClient.post<{ data: CustomerLoginResponse }>(
    '/customer-portal/auth/refresh',
    { refreshToken },
  );

  const tokens = resp.data.data;
  if (!tokens?.accessToken || !tokens?.refreshToken) {
    clearAuth();
    throw new Error('Malformed refresh response');
  }

  rotateTokens(tokens.accessToken, tokens.refreshToken);
  return tokens.accessToken;
}
